import {
  format,
  getDay,
  isAfter,
  isBefore,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { useDispatch, useSelector } from 'react-redux';

import 'react-big-calendar/lib/css/react-big-calendar.css';

import { Loader } from '@powdr/components';
import { DateFormats, FetchStatus } from '@powdr/constants';
import { getViewAll } from '@powdr/stores';
import {
  aliasContentBlockDataFromViews,
  parseEventDate,
} from '@powdr/utils';

import { EventCalendarEventWrapper, EventCalendarItem, EventCalendarToolbar } from './components';
import { StyledEventCalendar } from './styles';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
});

const getMonthsWithEvents = (events = []) => {
  const uniqueMonths = new Map();

  events.forEach((event) => {
    if (event?.start) {
      const startMonth = startOfMonth(event.start);
      uniqueMonths.set(startMonth.getTime(), startMonth);
    }

    if (event?.end) {
      const endMonth = startOfMonth(event.end);
      uniqueMonths.set(endMonth.getTime(), endMonth);
    }
  });

  return [...uniqueMonths.values()].sort((a, b) => a.getTime() - b.getTime());
};

const isMonthWithEvents = (monthsWithEvents, date) => {
  const targetMonth = startOfMonth(date);
  return monthsWithEvents.some((month) => month.getTime() === targetMonth.getTime());
};

export const EventCalendar = ({
  className = '',
  entityView,
  parentColorProfile = null,
}) => {
  const dispatch = useDispatch();
  const viewMachineName = entityView?.viewMachineName;
  const { drupal: calendarData, status } = useSelector((state) => state.drupal) || {};
  const viewData = calendarData?.[viewMachineName];
  const isCalendarLoading = viewData === undefined && status !== FetchStatus.ERROR;
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (viewMachineName) {
      dispatch(getViewAll(viewMachineName));
    }
  }, [dispatch, viewMachineName]);

  const events = useMemo(() => (
    viewData?.map((item) => {
      const content = aliasContentBlockDataFromViews(item);
      const start = parseEventDate(content.date);
      if (!start) return null;

      return {
        id: content.id,
        title: content.title,
        start,
        end: parseEventDate(content.endDate) || start,
        allDay: true,
        url: content.buttonLink,
      };
    }).filter(Boolean) || []
  ), [viewData]);

  const monthsWithEvents = useMemo(() => getMonthsWithEvents(events), [events]);
  const currentMonth = startOfMonth(currentDate);
  const previousMonthWithEvents = [...monthsWithEvents]
    .reverse()
    .find((month) => isBefore(month, currentMonth));
  const nextMonthWithEvents = monthsWithEvents.find(
    (month) => isAfter(month, currentMonth),
  );

  const goToPreviousMonthWithEvents = useCallback(() => {
    if (previousMonthWithEvents) {
      setCurrentDate(previousMonthWithEvents);
    }
  }, [previousMonthWithEvents]);

  const goToNextMonthWithEvents = useCallback(() => {
    if (nextMonthWithEvents) {
      setCurrentDate(nextMonthWithEvents);
    }
  }, [nextMonthWithEvents]);

  const handleNavigate = useCallback((newDate) => {
    if (isMonthWithEvents(monthsWithEvents, newDate)) {
      setCurrentDate(newDate);
    }
  }, [monthsWithEvents]);

  return (
    <StyledEventCalendar className={className} $colorProfile={parentColorProfile}>
      {isCalendarLoading ? (
        <Loader
          className="icon"
          classAnimationEffect="loader-animated spin infinite"
          iconName="dor-sunny"
          size="30"
        />
      ) : (
        <>
          <EventCalendarToolbar
            label={format(currentDate, DateFormats.MONTH_PLUS_YEAR_LONG)}
            hasPreviousMonth={Boolean(previousMonthWithEvents)}
            hasNextMonth={Boolean(nextMonthWithEvents)}
            onPreviousMonth={goToPreviousMonthWithEvents}
            onNextMonth={goToNextMonthWithEvents}
            colorProfile={parentColorProfile}
          />
          <BigCalendar
            localizer={localizer}
            events={events}
            date={currentDate}
            view={Views.MONTH}
            views={[Views.MONTH]}
            toolbar={false}
            onNavigate={handleNavigate}
            components={{
              event: EventCalendarItem,
              eventWrapper: EventCalendarEventWrapper,
            }}
            drilldownView={null}
            startAccessor="start"
            endAccessor="end"
            allDayAccessor="allDay"
            popup
            style={{ height: 700 }}
          />
        </>
      )}
    </StyledEventCalendar>
  );
};

EventCalendar.propTypes = {
  className: PropTypes.string,
  entityView: PropTypes.shape({
    viewMachineName: PropTypes.string,
  }).isRequired,
  parentColorProfile: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};
